/**
 * ChatAreaコンテンツUIコンポーネント
 * UIの描画を担当（画像生成機能統合）
 */

import { Ref } from 'react';
import { Box, Heading, Text } from '@chakra-ui/react';
import { ChatMessageItem } from './messages/ChatMessageItem';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from '@/components/loading/TypingIndicator';
import { ExamplePromptButton } from '@/components/input/ExamplePromptButton';
import { AgeGroup } from '@/constants';
import { DropOverlay } from '@/components/attachment/DropOverlay';
import type { PromptInputRef } from '@/components/input/PromptInput';
import type { ChatMessage, ChatLimitInfo, FileAttachment, AttachedFileInfo } from '@/types';

interface WelcomeMsg {
  greeting: string;
  subtitle: string;
  examplesTitle: string;
  examples: string[];
}

interface ChatAreaContentProps {
  // 状態
  messages: ChatMessage[];
  isLoading: boolean;
  isLoadingSession: boolean;
  chatUsage: ChatLimitInfo;
  isDragging: boolean;
  droppedFiles: FileAttachment[];
  dropError: string;
  welcomeMsg: WelcomeMsg;
  sidebarOpen: boolean;
  sessionId: string;
  ageGroup: AgeGroup;

  // refs
  messagesEndRef: Ref<HTMLDivElement>;
  chatInputRef: Ref<PromptInputRef>;

  // ハンドラー
  handleSendMessage: (text: string, attachedFileIds?: string[]) => Promise<void>;
  startUploading: (userText: string, attachedFiles?: AttachedFileInfo[]) => void;
  clearDroppedFiles: () => void;
  handleFileError: (error: string) => void;
  handlers: {
    onDragEnter: (e: React.DragEvent<HTMLElement>) => void;
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void;
    onDragOver: (e: React.DragEvent<HTMLElement>) => void;
    onDrop: (e: React.DragEvent<HTMLElement>) => void;
  };
  onStreamingComplete: (messageId: string) => void;
}

export const ChatAreaContent = ({
  messages,
  isLoading,
  isLoadingSession,
  chatUsage,
  isDragging,
  droppedFiles,
  dropError,
  welcomeMsg,
  sessionId,
  ageGroup,
  messagesEndRef,
  chatInputRef,
  handleSendMessage,
  startUploading,
  clearDroppedFiles,
  handleFileError,
  handlers,
  onStreamingComplete,
}: ChatAreaContentProps) => {
  return (
    <Box
      as="main"
      position="relative"
      display="flex"
      flex={1}
      flexDirection="column"
      overflow="hidden"
      bg="white"
      {...handlers}
    >
      <DropOverlay isDragging={isDragging} dropError={dropError} />

      {/* メッセージエリア */}
      <Box flex={1} overflowY="auto" bg="white" p={0}>
        {isLoadingSession ? (
          <Box display="flex" minH={75} flex={1} alignItems="center" justifyContent="center">
            <TypingIndicator />
          </Box>
        ) : (
          <>
            {messages.length === 0 && (
              <Box mx="auto" maxW="chat" px={8} py={16} textAlign="center">
                <Heading
                  as="h2"
                  m={0}
                  mb={4}
                  fontSize="4xl"
                  fontWeight="bold"
                  letterSpacing="tight"
                  color="gray.900"
                >
                  {welcomeMsg.greeting}
                </Heading>
                <Text
                  as="p"
                  m={0}
                  mb={12}
                  fontSize="lg"
                  fontWeight="medium"
                  lineHeight="relaxed"
                  color="gray.600"
                >
                  {welcomeMsg.subtitle}
                </Text>
              </Box>
            )}

            {messages.map((message) => (
              <ChatMessageItem
                key={message.id}
                message={message}
                onStreamingComplete={onStreamingComplete}
              />
            ))}
          </>
        )}

        <Box ref={messagesEndRef} />
      </Box>

      {/* 入力エリア */}
      <Box
        display="flex"
        flexDirection="column"
        gap={3}
        borderTop="1px solid"
        borderColor="gray.300"
        bg="white"
        p={4}
        boxShadow="sm"
      >
        {messages.length === 0 && (
          <Box mx="auto" display="flex" maxW="chat" alignItems="center" gap={3} textAlign="left">
            <Heading
              as="h3"
              m={0}
              whiteSpace="nowrap"
              fontSize="sm"
              fontWeight="medium"
              color="gray.600"
            >
              {welcomeMsg.examplesTitle}
            </Heading>
            <Box display="flex" flex={1} flexWrap="wrap" gap={2}>
              {welcomeMsg.examples.map((example, index) => (
                <ExamplePromptButton
                  key={index}
                  text={example}
                  onClick={() => handleSendMessage(example)}
                />
              ))}
            </Box>
          </Box>
        )}

        <ChatInput
          ref={chatInputRef}
          onSend={handleSendMessage}
          disabled={isLoading}
          ageGroup={ageGroup}
          sessionId={sessionId}
          chatUsage={chatUsage}
          onStartUploading={startUploading}
          droppedFiles={droppedFiles}
          onDroppedFilesProcessed={clearDroppedFiles}
          onFileError={handleFileError}
        />
      </Box>
    </Box>
  );
};
